/**
 * Structural equality and record patching for document values (W1-03 core-model-ops).
 *
 * Document records are immutable trees of plain objects, arrays and primitives (Beats, tuples, passthrough pair
 * lists, nested provenance). `valueEquals` compares them structurally:
 *  - numbers with `Object.is` (so -0 and 0 differ and NaN equals NaN);
 *  - arrays element-wise;
 *  - plain objects by their DEFINED fields (a field whose value is `undefined` counts as absent, as in patches).
 *
 * `applyPatch` implements the op patch semantics of core/ops/types.ts: shallow, `undefined` removes the field. It
 * compares with the same `Object.is` rule, so an op that `prevOf` records as a change is always applied as one.
 *
 * Negative zero: a document never holds -0. JSON cannot carry it and Phira cannot tell it from 0, so every way into
 * a live document maps it to 0 (`withoutNegativeZero`): container creation (a lifted file may write `-0.0`) and
 * every TxDraft write (`insert`, `update`, `updateSingleton`, `applyOps`). A command that negates 0 therefore
 * records nothing, recorded ops never carry -0, and snapshots of a container's document are plain-JSON exact.
 * `serializeDoc` / `deserializeDoc` and the op codec stay exact (they never change a number).
 */

/** Structural equality of document values (see the file header). */
export function valueEquals(a: unknown, b: unknown): boolean {
  if (typeof a === 'number' || typeof b === 'number') return Object.is(a, b);
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  const aArr = Array.isArray(a);
  if (aArr !== Array.isArray(b)) return false;
  if (aArr) {
    const x = a as readonly unknown[];
    const y = b as readonly unknown[];
    if (x.length !== y.length) return false;
    for (let i = 0; i < x.length; i++) if (!valueEquals(x[i], y[i])) return false;
    return true;
  }
  const x = a as Readonly<Record<string, unknown>>;
  const y = b as Readonly<Record<string, unknown>>;
  let xCount = 0;
  for (const k of Object.keys(x)) {
    const xv = x[k];
    if (xv === undefined) continue;
    xCount++;
    if (!valueEquals(xv, y[k])) return false;
  }
  let yCount = 0;
  for (const k of Object.keys(y)) if (y[k] !== undefined) yCount++;
  return xCount === yCount;
}

/** Own enumerable field of a record-like value (undefined when absent). */
export function fieldOf(rec: object, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(rec, key) ? (rec as Readonly<Record<string, unknown>>)[key] : undefined;
}

/**
 * Shallow patch: a copy of `rec` with every field of `patch` set, and fields whose patch value is `undefined`
 * removed. Returns `rec` itself when the patch changes nothing.
 */
export function applyPatch<R extends object>(rec: R, patch: object): R {
  let out: Record<string, unknown> | null = null;
  for (const key of Object.keys(patch)) {
    const next = (patch as Readonly<Record<string, unknown>>)[key];
    const has = Object.prototype.hasOwnProperty.call(rec, key);
    const cur = has ? (rec as Readonly<Record<string, unknown>>)[key] : undefined;
    if (next === undefined ? !has : has && Object.is(cur, next)) continue;
    out ??= { ...(rec as Readonly<Record<string, unknown>>) };
    if (next === undefined) Reflect.deleteProperty(out, key);
    else out[key] = next;
  }
  return out === null ? rec : (out as R);
}

/**
 * The `prev` side of a patch: for every field of `patch`, the current value of `rec` (undefined when absent).
 * Only fields whose value actually changes are kept when `onlyChanged` is set.
 */
export function prevOf(
  rec: object,
  patch: object,
  onlyChanged: boolean,
): { prev: Record<string, unknown>; next: Record<string, unknown> } {
  const prev: Record<string, unknown> = {};
  const next: Record<string, unknown> = {};
  for (const key of Object.keys(patch)) {
    const nv = (patch as Readonly<Record<string, unknown>>)[key];
    const cv = fieldOf(rec, key);
    if (onlyChanged && valueEquals(cv, nv)) continue;
    prev[key] = cv;
    next[key] = nv;
  }
  return { prev, next };
}

/** True when the record matches every field of `expected` (undefined = the field is absent). */
export function matchesFields(rec: object, expected: object): boolean {
  for (const key of Object.keys(expected)) {
    if (!valueEquals(fieldOf(rec, key), (expected as Readonly<Record<string, unknown>>)[key])) return false;
  }
  return true;
}

/** Sets an own field (also `__proto__`, which plain assignment would turn into a prototype change). */
function setOwn(out: Record<string, unknown>, key: string, value: unknown): void {
  if (key === '__proto__')
    Object.defineProperty(out, key, { value, enumerable: true, writable: true, configurable: true });
  else out[key] = value;
}

/**
 * Copy-on-write walk of a document value: drops `undefined`-valued object fields (`dropUndefined`) and maps -0 to 0
 * (`positiveZero`). Unchanged sub-trees keep their identity; the input is never mutated.
 */
function canon<T>(value: T, dropUndefined: boolean, positiveZero: boolean): T {
  if (typeof value === 'number') return (positiveZero && Object.is(value, -0) ? 0 : value) as T;
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    let out: unknown[] | null = null;
    const arr = value as readonly unknown[];
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      const s = canon(v, dropUndefined, positiveZero);
      if (!Object.is(s, v)) {
        out ??= arr.slice();
        out[i] = s;
      }
    }
    return (out ?? value) as T;
  }
  const obj = value as Readonly<Record<string, unknown>>;
  let out: Record<string, unknown> | null = null;
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (v === undefined) {
      if (dropUndefined) {
        out ??= { ...obj };
        Reflect.deleteProperty(out, key);
      }
      continue;
    }
    const s = canon(v, dropUndefined, positiveZero);
    if (!Object.is(s, v)) {
      out ??= { ...obj };
      setOwn(out, key, s);
    }
  }
  return (out ?? value) as T;
}

/** Deep copy without `undefined`-valued object fields; returns the same reference when there is nothing to strip. */
export function stripUndefined<T>(value: T): T {
  return canon(value, true, false);
}

/**
 * Deep copy with every -0 replaced by 0 (`undefined` fields kept, so patches keep their field removals); returns the
 * same reference when there is no -0.
 */
export function withoutNegativeZero<T>(value: T): T {
  // Almost no record holds a -0: a scan without allocations first (W3-03: every record of a document goes through
  // here when a container is created), the copying walk only when there is one.
  return hasNegativeZero(value) ? canon(value, false, true) : value;
}

/** True when a -0 occurs anywhere in a plain value (numbers, arrays, plain objects). */
export function hasNegativeZero(value: unknown): boolean {
  if (typeof value === 'number') return value === 0 && 1 / value < 0;
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) {
    const arr = value as readonly unknown[];
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (typeof v === 'number' ? v === 0 && 1 / v < 0 : typeof v === 'object' && v !== null && hasNegativeZero(v)) {
        return true;
      }
    }
    return false;
  }
  const obj = value as Readonly<Record<string, unknown>>;
  // for-in (no key array per object); document values are plain objects, nothing enumerable is inherited.
  for (const key in obj) {
    const v = obj[key];
    if (typeof v === 'number' ? v === 0 && 1 / v < 0 : typeof v === 'object' && v !== null && hasNegativeZero(v)) {
      return true;
    }
  }
  return false;
}
